import React from 'react';
import {
  ArrowsClockwise, Download, Warning,
} from '@phosphor-icons/react';
import { Button } from '../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '../components/ui/tabs';
import RealTimeIndicator from '../components/RealTimeIndicator';
import { AgencyComparisonTab } from './cross-agency/AgencyComparisonTab';
import { AgencyRankingsTab } from './cross-agency/AgencyRankingsTab';
import { AgencyTrendsTab } from './cross-agency/AgencyTrendsTab';
import { CrossAgencyKpiCards } from './cross-agency/CrossAgencyKpiCards';
import { useCrossAgencyReports } from './cross-agency/useCrossAgencyReports';

const AccessRestricted = () => (
  <div className="flex items-center justify-center h-96">
    <div className="text-center">
      <Warning size={48} className="mx-auto mb-4 text-amber-500" />
      <h2 className="text-xl font-bold text-slate-800">Access Restricted</h2>
      <p className="text-slate-500">
        Only AZAI Super Admins and Billing Managers can access cross-agency reports.
      </p>
    </div>
  </div>
);

const LoadingSpinner = () => (
  <div className="flex items-center justify-center h-96">
    <div className="w-12 h-12 border-4 border-indigo-900 border-t-transparent rounded-full animate-spin" />
  </div>
);

/**
 * Cross-Agency Reports — orchestrator. The 3 tabs (Comparison / Rankings /
 * Trends) live in `pages/cross-agency/`. State + sort logic + ranking math
 * lives in `useCrossAgencyReports`.
 */
const CrossAgencyReportsPage = () => {
  const c = useCrossAgencyReports();

  if (!c.canViewReports) return <AccessRestricted />;
  if (c.loading) return <LoadingSpinner />;
  if (!c.stats) return null;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="cross-agency-reports-page">
      <header className="flex justify-between items-start">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
              Cross-Agency Reports
            </h1>
            <RealTimeIndicator size="small" />
          </div>
          <p className="text-slate-500 mt-1">Aggregate analytics and agency comparisons</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={c.timeRange} onValueChange={c.setTimeRange}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">This Week</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="quarter">This Quarter</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
              <SelectItem value="all">All Time</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={c.loadData}>
            <ArrowsClockwise size={16} />
          </Button>
          <Button variant="outline" size="sm" onClick={() => c.handleExport('csv')}>
            <Download size={16} className="mr-1" />
            CSV
          </Button>
        </div>
      </header>

      <CrossAgencyKpiCards
        totals={c.totals}
        activeAgencies={c.stats.active_agencies}
      />

      <Tabs defaultValue="comparison" className="space-y-4">
        <TabsList>
          <TabsTrigger value="comparison">Agency Comparison</TabsTrigger>
          <TabsTrigger value="rankings">Rankings & Performance</TabsTrigger>
          <TabsTrigger value="trends">Trend Analysis</TabsTrigger>
        </TabsList>

        <TabsContent value="comparison">
          <AgencyComparisonTab
            rankedAgencies={c.rankedAgencies}
            sortBy={c.sortBy}
            setSortBy={c.setSortBy}
            sortOrder={c.sortOrder}
            setSortOrder={c.setSortOrder}
          />
        </TabsContent>

        <TabsContent value="rankings">
          <AgencyRankingsTab
            rankedAgencies={c.rankedAgencies}
            slices={c.slices}
          />
        </TabsContent>

        <TabsContent value="trends">
          <AgencyTrendsTab stats={c.stats} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default CrossAgencyReportsPage;
